import { useMutation } from "@tanstack/react-query";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { FinancialConnection } from "@common/types/financialConnection";
import { useCallback } from "react";
import { Entity } from "@common/types/entity";
import {
    GustoConnectionForEntity,
    fixGustoConnection,
    syncGustoConnection,
} from "@lib/gusto";
import {
    addIntegrationConnectionInQueryData,
    entitiesAccountsQueryKey,
} from "@queries/entitiesAccounts";
import { useConnectAccount } from "@components/connectAccount/ConnectAccount.context";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useScope } from "@state/scopeContext.lib";
import { invalidateAccountRelatedQueries } from "@api/account.api";
import { useCreateGustoConnectionMutation } from "@api/gusto.api";
import { integrationConnectionQueryKeys } from "@api/integrationConnection.api";
import {
    getPayrollQueryKey,
    gustoConnectionsQueryKey,
    queryClient,
} from "../queryClient";

export function useGustoConnectionCreationMutation(onSuccess?: () => void) {
    const { toast } = useToaster();
    const { updateConnectAccountState } = useConnectAccount();
    const { workspaceId } = useScope();
    const onSuccessCallback = useCallback(
        (connection?: IntegrationConnection) => {
            if (!connection) {
                return;
            }
            addIntegrationConnectionInQueryData(queryClient, connection);
            queryClient.invalidateQueries({
                queryKey: integrationConnectionQueryKeys.all,
            });
            invalidateAccountRelatedQueries({ workspaceId });
            const hasAccountsNotReadyForSync =
                connection.integrationAccounts?.some(
                    (a) => !a.financialAccount?.entityId,
                );
            if (hasAccountsNotReadyForSync) {
                updateConnectAccountState({
                    isConnecting: false,
                    integrationConnection: connection,
                    connectionSaved: true,
                    foreign: [],
                });
                toast(
                    "Connected your Gusto account. Payroll transactions will start syncing once you select an entity.",
                );
            } else {
                toast(
                    "Connected your Gusto account. Payroll transactions will sync in a few minutes.",
                );
            }
            onSuccess?.();
        },
        [onSuccess, updateConnectAccountState, toast, workspaceId],
    );
    return useCreateGustoConnectionMutation(onSuccessCallback);
}

export function useSyncGustoConnectionMutation(entity: Entity) {
    const { toast } = useToaster();
    const { workspaceId } = useScope();
    return useMutation({
        mutationFn: (connection: GustoConnectionForEntity) => {
            if (!workspaceId) {
                throw new Error("No workspace selected");
            }
            return syncGustoConnection(connection, workspaceId);
        },

        onSuccess: async () => {
            const key = getPayrollQueryKey(entity.id);
            await queryClient.invalidateQueries({ queryKey: key });
            queryClient.invalidateQueries({
                queryKey: [entitiesAccountsQueryKey],
            });
            queryClient.invalidateQueries({
                queryKey: integrationConnectionQueryKeys.all,
            });
            queryClient.invalidateQueries({
                queryKey: gustoConnectionsQueryKey,
            });
            toast(`Connection synced`);
        },
        onError: () => {
            queryClient.invalidateQueries({
                queryKey: [entitiesAccountsQueryKey],
            });
            queryClient.invalidateQueries({
                queryKey: integrationConnectionQueryKeys.all,
            });
        },
    });
}

interface FixGustoConnectionPayload {
    connection: FinancialConnection;
    code: string;
}

export function useFixGustoConnectionsMutation(onSuccess?: () => unknown) {
    const { error } = useToaster();
    const { workspaceId } = useScope();
    return useMutation({
        mutationFn: (payload: FixGustoConnectionPayload) => {
            if (!workspaceId) {
                throw new Error("No workspace selected");
            }
            return fixGustoConnection(
                payload.connection,
                payload.code,
                workspaceId,
            );
        },

        onSuccess: (connected) => {
            if (!connected) {
                return;
            }
            // The legacy connection is now migrated: drop it from the legacy
            // list and surface the new integration connection instead.
            addIntegrationConnectionInQueryData(queryClient, connected);
            queryClient.invalidateQueries({
                queryKey: gustoConnectionsQueryKey,
            });
            queryClient.invalidateQueries({
                queryKey: integrationConnectionQueryKeys.all,
            });
            queryClient.invalidateQueries({
                queryKey: [entitiesAccountsQueryKey],
            });
            onSuccess?.();
        },
        onError: () => {
            error("Failed to fix your Gusto connection. Please try again.");
        },
    });
}
